/**
 * Hook useDossierActions
 * Centralise la logique métier pour EditDossier
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { API, toast } from '../../../App';

export const useDossierActions = ({ dossierId, onDossierUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [dossier, setDossier] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [starting, setStarting] = useState(false);
  const [savingItem, setSavingItem] = useState(null);
  const [itemDrafts, setItemDrafts] = useState({});
  const [publishingItem, setPublishingItem] = useState(null);
  const [savingJ0, setSavingJ0] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const pollRef = useRef(null);

  // Chargement du dossier
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await axios.get(`${API}/auto-dossiers/${dossierId}`);
      setDossier(data.dossier);
      if (!silent) setItemDrafts({});
      if (onDossierUpdate) onDossierUpdate(data.dossier);
      return data.dossier;
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.detail || 'Chargement échoué');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [dossierId, onDossierUpdate]);

  // Auto-refresh pendant la génération
  useEffect(() => {
    const isGenerating = dossier?.status === 'articles_generating' || dossier?.items?.some(i => i.status === 'generating');
    if (isGenerating && !pollRef.current) {
      pollRef.current = setInterval(() => load(true), 5000);
    } else if (!isGenerating && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [dossier?.status, dossier?.items, load]);

  // Charger au montage
  useEffect(() => { load(); }, [dossierId, load]);

  // États dérivés
  const hasAdvancedItems = dossier?.items?.some(i => 
    ['generated', 'generating', 'published', 'scheduled', 'publishing'].includes(i.status)
  );
  const hasPublishedOrScheduled = dossier?.items?.some(i => 
    ['published', 'scheduled'].includes(i.status)
  );
  const allItemsGenerated = dossier?.items?.length > 0 && dossier.items.every(i => i.status === 'generated');
  const canStartGeneration = dossier?.items?.length > 0 
    && !['articles_generating'].includes(dossier?.status)
    && !hasPublishedOrScheduled;

  // Générer le plan
  const generatePlan = async () => {
    if (hasAdvancedItems) {
      toast.error('Régénération bloquée : des articles ont déjà été produits ou publiés.');
      return;
    }
    if (dossier?.items?.length && !window.confirm('Le plan existant sera remplacé. Continuer ?')) return;
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/auto-dossiers/${dossierId}/generate-plan`);
      setDossier(data.dossier);
      setItemDrafts({});
      toast.success(`Plan généré : ${data.stats.items_count} article(s), ${data.stats.links_count} lien(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Génération échouée');
    } finally {
      setGenerating(false);
    }
  };

  // Annuler le plan
  const resetPlan = async () => {
    if (hasAdvancedItems) {
      toast.error('Annulation impossible : des articles ont déjà été générés ou publiés.');
      return;
    }
    if (!window.confirm(
      'Annuler le plan et revenir au paramétrage ?\n\n'
      + `Les ${dossier.items.length} article(s) planifié(s) et leurs liens seront supprimés. `
      + 'Le mot-clé principal, les sites sélectionnés et les URLs sources restent intacts. '
      + 'Vous pourrez relancer la génération du plan ensuite.'
    )) return;
    try {
      const { data } = await axios.post(`${API}/auto-dossiers/${dossierId}/reset-plan`);
      setDossier(data.dossier);
      setItemDrafts({});
      toast.success(`Plan annulé : ${data.items_deleted} article(s) et ${data.links_deleted} lien(s) supprimés`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Annulation échouée');
    }
  };

  // Date par défaut pour la génération programmée (01h00)
  const defaultScheduleDate = () => {
    const d = new Date();
    d.setHours(1, 0, 0, 0);
    if (d.getTime() <= Date.now()) {
      d.setDate(d.getDate() + 1);
    }
    const tz = d.getTimezoneOffset();
    const local = new Date(d.getTime() - tz * 60000);
    return local.toISOString().slice(0, 16);
  };

  // Exécution génération immédiate
  const proceedWithGeneration = async () => {
    try {
      await axios.post(`${API}/auto-dossiers/${dossierId}/start-generation`);
      toast.success('Génération lancée. Le suivi se rafraîchit automatiquement.');
      await load(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Lancement échoué');
    } finally {
      setStarting(false);
    }
  };

  // Programmation effective
  const proceedWithScheduling = async (localIso) => {
    try {
      const isoUtc = new Date(localIso).toISOString();
      const { data } = await axios.post(
        `${API}/auto-dossiers/${dossierId}/schedule-generation`,
        { scheduled_at: isoUtc }
      );
      toast.success(
        `Génération programmée pour le ${new Date(data.scheduled_generation_at).toLocaleString('fr-FR')}`
      );
      await load(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Programmation échouée');
    } finally {
      setStarting(false);
    }
  };

  // Scraper les sources
  const scrapeSourcesAndHandle = async (mode, scheduledIso, onScrapeResult) => {
    setStarting(true);
    try {
      const { data } = await axios.post(`${API}/auto-dossiers/${dossierId}/scrape-sources`, {});
      
      if (data.documents_only) {
        const confirmMsg = 
          `⚠️ ATTENTION : Génération basée sur documents uniquement\n\n` +
          `Vous n'avez fourni aucune URL à scraper.\n` +
          `La génération sera basée uniquement sur ${data.document_count} document(s) annexe(s).\n\n` +
          `Voulez-vous continuer quand même ?`;
        if (!window.confirm(confirmMsg)) {
          setStarting(false);
          return;
        }
        if (mode === 'schedule' && scheduledIso) {
          await proceedWithScheduling(scheduledIso);
        } else {
          await proceedWithGeneration();
        }
        return;
      }
      
      if (data.all_success) {
        if (mode === 'schedule' && scheduledIso) {
          await proceedWithScheduling(scheduledIso);
        } else {
          await proceedWithGeneration();
        }
      } else {
        onScrapeResult({
          sources: data.sources || [],
          succeeded: data.succeeded || 0,
          failed: data.failed || 0,
          total: data.total || 0,
          mode,
          scheduledIso,
        });
        setStarting(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scraping échoué');
      setStarting(false);
    }
  };

  // Continuer avec les sources existantes (enlever les échecs)
  const continueWithExistingSources = async (scrapeModal) => {
    const mode = scrapeModal?.mode || 'schedule';
    const scheduledIso = scrapeModal?.scheduledIso;
    setStarting(true);
    try {
      const failedUrls = (scrapeModal?.sources || []).filter(s => !s.success).map(s => s.url);
      if (failedUrls.length > 0) {
        await axios.post(`${API}/auto-dossiers/${dossierId}/remove-source-urls`, { urls: failedUrls });
      }
      if (mode === 'schedule' && scheduledIso) {
        await proceedWithScheduling(scheduledIso);
      } else {
        await proceedWithGeneration();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Lancement échoué');
      setStarting(false);
    }
  };

  // Ajouter des URLs et re-scraper
  const addUrlsAndRetry = async (newUrls, scrapeModal, onScrapeResult) => {
    if (newUrls.length === 0) {
      return toast.error('Saisissez au moins une URL');
    }
    const mode = scrapeModal?.mode || 'schedule';
    const scheduledIso = scrapeModal?.scheduledIso;
    setStarting(true);
    try {
      const { data } = await axios.post(
        `${API}/auto-dossiers/${dossierId}/scrape-sources`,
        { additional_urls: newUrls }
      );
      if (data.all_success) {
        if (mode === 'schedule' && scheduledIso) {
          await proceedWithScheduling(scheduledIso);
        } else {
          await proceedWithGeneration();
        }
        return { success: true };
      } else {
        onScrapeResult({
          sources: data.sources || [],
          succeeded: data.succeeded || 0,
          failed: data.failed || 0,
          total: data.total || 0,
          mode,
          scheduledIso,
        });
        setStarting(false);
        return { success: false };
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Re-scraping échoué');
      setStarting(false);
      return { success: false };
    }
  };

  // Annuler la programmation
  const cancelScheduledGeneration = async () => {
    if (!window.confirm('Annuler la programmation de la génération ?')) return;
    try {
      await axios.post(`${API}/auto-dossiers/${dossierId}/cancel-scheduled-generation`);
      toast.success('Programmation annulée');
      await load(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Annulation échouée');
    }
  };

  // Sauvegarder les drafts d'un item
  const setDraft = (itemId, field, value) => {
    setItemDrafts(d => ({ ...d, [itemId]: { ...(d[itemId] || {}), [field]: value } }));
  };

  const saveItem = async (item) => {
    const draft = itemDrafts[item.id] || {};
    if (Object.keys(draft).length === 0) return;
    setSavingItem(item.id);
    try {
      const payload = { ...draft };
      if (payload.secondary_keywords && typeof payload.secondary_keywords === 'string') {
        payload.secondary_keywords = payload.secondary_keywords.split(',').map(s => s.trim()).filter(Boolean);
      }
      if (payload.planned_publish_offset_days !== undefined) {
        payload.planned_publish_offset_days = parseInt(payload.planned_publish_offset_days, 10) || 0;
      }
      const { data } = await axios.patch(`${API}/auto-dossier-items/${item.id}`, payload);
      toast.success('Item enregistré');
      setDossier(d => ({
        ...d,
        items: d.items.map(it => it.id === item.id ? { ...it, ...data.item } : it)
      }));
      setItemDrafts(d => { const { [item.id]: _, ...rest } = d; return rest; });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Échec enregistrement');
    } finally {
      setSavingItem(null);
    }
  };

  // Sauvegarder la date J0
  const saveJ0 = async (j0Iso) => {
    setSavingJ0(true);
    try {
      const { data } = await axios.patch(`${API}/auto-dossiers/${dossierId}`, { j0_date: j0Iso || null });
      setDossier(d => ({ ...d, ...data.dossier }));
      toast.success(j0Iso ? 'Date J0 enregistrée' : 'Date J0 effacée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Échec MAJ J0');
    } finally {
      setSavingJ0(false);
    }
  };

  // Publier un item
  const publishItem = async (item, opts, onComplete) => {
    setPublishingItem(item.id);
    try {
      const { data } = await axios.post(`${API}/auto-dossier-items/${item.id}/publish`, opts);
      if (opts.publish_status === 'future') {
        toast.success(`Planifié pour le ${new Date(opts.scheduled_date).toLocaleString('fr-FR')}`);
      } else {
        toast.success(data.reconciled_links_count > 0
          ? `Publié ! (${data.reconciled_links_count} lien(s) réconcilié(s) dans d'autres articles)`
          : 'Article publié sur WordPress');
      }
      await load(true);
      if (onComplete) onComplete();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      let msg;
      if (status && detail) {
        msg = `Publication échouée (HTTP ${status}) : ${detail}`;
      } else if (status) {
        msg = `Publication échouée (HTTP ${status}) : réponse serveur non exploitable`;
      } else if (err.code === 'ECONNABORTED') {
        msg = 'Publication échouée : timeout serveur. L\'article est peut-être quand même publié, vérifie sur WordPress avant de relancer.';
      } else if (err.message) {
        msg = `Publication échouée : ${err.message} (réseau / CORS / timeout)`;
      } else {
        msg = 'Publication échouée (erreur inconnue, voir console)';
      }
      console.error('[publishItem] axios error:', err);
      toast.error(msg);
      try { await load(true); } catch (_) { /* silent */ }
    } finally {
      setPublishingItem(null);
      if (onComplete) onComplete();
    }
  };

  // Confirmer programmation génération
  const confirmScheduleGeneration = async (schedGenDate, onComplete) => {
    if (!schedGenDate) return toast.error('Choisissez une date');
    const localDate = new Date(schedGenDate);
    if (Number.isNaN(localDate.getTime())) return toast.error('Date invalide');
    const isoUtc = localDate.toISOString();
    setScheduling(true);
    try {
      const { data: scrapeData } = await axios.post(
        `${API}/auto-dossiers/${dossierId}/scrape-sources`, {}
      );
      if (!scrapeData.all_success) {
        setScheduling(false);
        toast.warning("Échec partiel du scraping. Résolvez d'abord les sources avant de programmer.");
        return { success: false, scrapeData };
      }
      const { data } = await axios.post(
        `${API}/auto-dossiers/${dossierId}/schedule-generation`,
        { scheduled_at: isoUtc }
      );
      toast.success(
        `Génération programmée pour le ${new Date(data.scheduled_generation_at).toLocaleString('fr-FR')}`
      );
      await load(true);
      if (onComplete) onComplete();
      return { success: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Programmation échouée');
      return { success: false };
    } finally {
      setScheduling(false);
    }
  };

  return {
    // États
    loading,
    dossier,
    generating,
    starting,
    savingItem,
    itemDrafts,
    publishingItem,
    savingJ0,
    scheduling,
    regenerating,
    
    // États dérivés
    hasAdvancedItems,
    hasPublishedOrScheduled,
    allItemsGenerated,
    canStartGeneration,
    
    // Actions
    load,
    generatePlan,
    resetPlan,
    defaultScheduleDate,
    proceedWithGeneration,
    proceedWithScheduling,
    scrapeSourcesAndHandle,
    continueWithExistingSources,
    addUrlsAndRetry,
    cancelScheduledGeneration,
    setDraft,
    saveItem,
    saveJ0,
    publishItem,
    confirmScheduleGeneration,
    setStarting,
  };
};

export default useDossierActions;
